import { useLayoutEffect, useState, RefObject } from 'react';

export type DropdownPlacement = 'bottom' | 'top';

export type DropdownPosition = {
  left: number;
  width: number;
  maxHeight: number;
  placement: DropdownPlacement;
  /** Set when placement === 'bottom' (anchors the menu top to the trigger bottom). */
  top?: number;
  /** Set when placement === 'top' (anchors the menu bottom to the trigger top). */
  bottom?: number;
};

/**
 * Computes viewport-relative (`position: fixed`) coordinates for a dropdown menu
 * rendered in a portal, so it escapes any ancestor `overflow` clipping (scroll
 * containers, drawers, fixed footers, etc.).
 *
 * The menu opens downward by default and flips upward when there isn't enough
 * room below and there is more room above. `maxHeight` is clamped to the space
 * available inside the trigger's nearest scrollable ancestor (intersected with
 * the viewport), so the menu stops just above sticky footers / action bars
 * (Submit, Back) instead of rendering over them, and scrolls internally rather
 * than being cut off.
 *
 * @param triggerRef   Ref to the element the menu is anchored to.
 * @param isOpen       Whether the menu is currently shown.
 * @param desiredHeight Preferred menu height in px (content-based).
 * @param deps         Extra dependencies that should trigger a recompute
 *                     (e.g. window size, option count).
 */
const useDropdownPosition = (
  triggerRef: RefObject<HTMLElement | null>,
  isOpen: boolean,
  desiredHeight: number,
  deps: unknown[] = []
): DropdownPosition | null => {
  const [position, setPosition] = useState<DropdownPosition | null>(null);

  useLayoutEffect(() => {
    if (!isOpen || !triggerRef.current) {
      setPosition(null);
      return;
    }

    // Walk up from the trigger and intersect the bounds of every scrollable
    // ancestor with the viewport. The result is the region the menu is allowed
    // to occupy — its lower edge lands on the bottom of the scroll area, i.e.
    // just above any sticky footer / action bar that sits below it.
    const getClipBounds = (el: HTMLElement) => {
      let top = 0;
      let bottom = window.innerHeight;
      let node: HTMLElement | null = el.parentElement;
      while (
        node &&
        node !== document.body &&
        node !== document.documentElement
      ) {
        const overflowY = window.getComputedStyle(node).overflowY;
        if (overflowY === 'auto' || overflowY === 'scroll') {
          const r = node.getBoundingClientRect();
          top = Math.max(top, r.top);
          bottom = Math.min(bottom, r.bottom);
        }
        node = node.parentElement;
      }
      return { top, bottom };
    };

    const compute = () => {
      const el = triggerRef.current;
      if (!el) return;

      const rect = el.getBoundingClientRect();
      const bounds = getClipBounds(el);
      const viewportHeight = window.innerHeight;
      const margin = 8; // keep the menu off the very edge of the boundary

      const spaceBelow = bounds.bottom - rect.bottom - margin;
      const spaceAbove = rect.top - bounds.top - margin;

      // Prefer opening downward (clamped to the boundary above the footer, per
      // spec). Only flip upward when there isn't even a usable amount of room
      // below and there's more space above.
      const minUsable = 96; // ~2 rows
      const openUpward = spaceBelow < minUsable && spaceAbove > spaceBelow;
      const available = openUpward ? spaceAbove : spaceBelow;

      setPosition({
        left: rect.left,
        width: rect.width,
        // Never smaller than a single row; never taller than the space available.
        maxHeight: Math.max(Math.min(desiredHeight, available), 56),
        placement: openUpward ? 'top' : 'bottom',
        top: openUpward ? undefined : rect.bottom,
        bottom: openUpward ? viewportHeight - rect.top : undefined,
      });
    };

    compute();

    // Recompute while any ancestor scrolls (capture phase catches inner
    // scroll containers) and on resize / orientation change.
    window.addEventListener('scroll', compute, true);
    window.addEventListener('resize', compute);
    return () => {
      window.removeEventListener('scroll', compute, true);
      window.removeEventListener('resize', compute);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, desiredHeight, ...deps]);

  return position;
};

export default useDropdownPosition;
